import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { QuestionIcon } from "components/icons/QuestionIcon";
import { DISPLAY_DATE } from "constants/date";
import { useToast } from "hooks/useToast";
import { useGetFileUploadQBOfileQuery } from "store/apis/qbo";
import { BackendError } from "types/utils/error";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { DownloadIcon } from "components/icons/Download";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDownloadFile } from "hooks/useDownloadFile";
import { HowItWorksModal } from "./HowItWorksModal";
import { formatDate } from "utils/formatDate";
import { GetQBOFileResponse } from "types/Models/books";

type QBOImportReviewProps = {
  onNext: () => void;
  entityId: string;
  isOpen: boolean;
  onClose: () => void;
};

type WhereToFindItemProps = {
  label: string;
  description: string;
};

const WHERE_TO_FIND_ITEMS: WhereToFindItemProps[] = [
  {
    label: "1. Chart of Accounts:",
    description: "Reports → Account List → Export to CSV",
  },
  {
    label: "2. Balance sheet:",
    description:
      'Reports → Set "Report period" as previous financial year period or "To" date as the last date of previous financial year → Set "Accounting method" as cash/accrual, based on customer\'s bookkeeping → Export to CSV',
  },
  {
    label: "3. Vendors:",
    description:
      "Settings → Export data → Select all checkboxes in Reports and Lists → Select date range → Export",
  },
  {
    label: "4. Customers:",
    description:
      "Settings → Export data → Select all checkboxes in Reports and Lists → Select date range → Export",
  },
  {
    label: "5. Journal Entries:",
    description:
      "Reports → Set date range from the last date of previous financial year till the date import needs to be done → Export to CSV",
  },
  {
    label: "6. Invoices and Received Payments report:",
    description:
      "Reports → Set date range from the last date of previous financial year till the date import needs to be done → Export to CSV",
  },
  {
    label: "7. Bills and Applied Payments report:",
    description:
      "Reports → Set date range from the last date of previous financial year till the date import needs to be done → Export to CSV",
  },
];

const WhereToFindItem = ({ label, description }: WhereToFindItemProps) => (
  <div>
    <span className="t-text-text-100 t-font-medium t-text-subtext-sm">
      {label}
    </span>{" "}
    <span className="t-text-text-60 t-text-subtext-sm">{description}</span>
  </div>
);

export const QBOWhereToFindAccordionContent = () => {
  return (
    <Accordion.Root
      type="multiple"
      className="t-flex t-flex-col t-gap-4 t-mt-2"
    >
      <Accordion.Item value="Where to find on Quickbooks">
        <Accordion.Trigger className="sm:t-p-3 sm:t-text-body-sm">
          <div className="t-flex t-items-center t-gap-2">
            <QuestionIcon />
            Where to find on Quickbooks
          </div>
        </Accordion.Trigger>

        <Accordion.Content className="t-flex t-flex-col t-gap-6">
          <Accordion.ItemGrid>
            <div className="t-col-span-2 t-flex t-flex-col t-gap-3 t-font-normal t-text-subtext">
              {WHERE_TO_FIND_ITEMS.map((item) => (
                <WhereToFindItem key={item.label} {...item} />
              ))}
            </div>
          </Accordion.ItemGrid>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};

export type QBOImportDocumentRowProps = {
  label: string;
  file?: GetQBOFileResponse["coa_file"];
  isDownloading: boolean;
  onDownloadFile: (fileId: string) => void;
};

export const QBOImportDocumentRow = ({
  label,
  file,
  isDownloading,
  onDownloadFile,
}: QBOImportDocumentRowProps) => {
  const handleDownloadFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!file) return;
    onDownloadFile(file.uuid);
  };

  return (
    <div className="t-flex t-flex-col t-gap-2">
      <label className="t-text-body-sm t-text-text-30">{label}</label>
      {file ? (
        <FilePreviewTag file={file} size="small" block>
          <Button
            customType="ghost_icon"
            size="small"
            disabled={isDownloading}
            isLoading={isDownloading}
            onClick={handleDownloadFile}
          >
            <DownloadIcon color="currentColor" />
          </Button>
        </FilePreviewTag>
      ) : (
        <div className="t-text-body-sm t-text-text-100 t-p-3 t-border t-border-neutral-0 t-rounded t-bg-surface-lighter-grey">
          Not uploaded
        </div>
      )}
    </div>
  );
};

export const QBOImportReview = ({
  onNext,
  entityId,
  isOpen,
  onClose,
}: QBOImportReviewProps) => {
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const { downloadFile, result: downloadState } = useDownloadFile();

  const { data, isLoading, isSuccess, error } = useGetFileUploadQBOfileQuery(
    { entityId },
    {
      skip: !entityId || !isOpen,
    }
  );

  if (error) {
    alertToast(
      { message: (error as BackendError).data?.error?.message },
      error as Error
    );
  }

  const handleDownloadFile = (fileId: string) => {
    downloadFile({ file_id: fileId, group_id: groupId });
  };

  const documentRows: Omit<
    QBOImportDocumentRowProps,
    "isDownloading" | "onDownloadFile"
  >[] = [
    { label: "Chart of Accounts", file: data?.coa_file },
    {
      label: "Previous financial year's Balance sheet",
      file: data?.balance_sheet_file,
    },
    { label: "Vendors List", file: data?.vendor_file },
    { label: "Customers List", file: data?.customer_file },
    { label: "Journal Entries", file: data?.je_file },
    {
      label: "Invoices and Received Payments report",
      file: data?.invoice_file,
    },
    { label: "Bills and Applied Payments report", file: data?.bill_file },
  ];

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose} modal={false}>
      <Modal.Content size="xl" useCustomOverlay>
        <Modal.Header>
          <div className="t-flex t-flex-col t-gap-2">
            <Modal.Title>Imported data from Quickbooks</Modal.Title>
          </div>
          <Modal.Close />
        </Modal.Header>
        <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={!data}>
          <Async.Empty>
            <Modal.Body className="t-text-center t-h-60 t-text-body-sm t-text-text-100 t-flex t-items-center t-justify-center sm:t-px-4">
              No import data found
            </Modal.Body>
          </Async.Empty>
          <Async.Success>
            <Modal.Body className="t-flex t-flex-col t-gap-5 sm:t-px-4">
              <div className="t-flex t-flex-col t-gap-2">
                <label className="t-text-body-sm t-text-text-30">
                  Date till which data needs to be imported from Quickbooks
                </label>
                <div className="t-text-body-sm t-text-text-100 t-border t-border-neutral-0 t-rounded ">
                  {data?.date_till_import
                    ? formatDate(data.date_till_import)
                    : "-"}
                </div>
              </div>

              <div className="t-flex t-flex-col t-gap-2">
                <label className="t-text-body-sm t-text-text-30">
                  Previous financial year's end date
                </label>
                <div className="t-text-body-sm t-text-text-100  t-border t-border-neutral-0 t-rounded ">
                  {data?.prev_fy_end_date
                    ? formatDate(data.prev_fy_end_date)
                    : "-"}
                </div>
              </div>

              <div className="t-text-sm t-font-medium t-mt-2">
                Documents
                <QBOWhereToFindAccordionContent />
              </div>

              {documentRows.map((row) => (
                <QBOImportDocumentRow
                  key={row.label}
                  {...row}
                  isDownloading={downloadState.isFetching}
                  onDownloadFile={handleDownloadFile}
                />
              ))}
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <HowItWorksModal />
              <Modal.RawClose asChild>
                <Button>Close</Button>
              </Modal.RawClose>
            </Modal.FooterButtonGroup>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
